import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector4 } from '@babylonjs/core/Maths/math.vector';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

import { as2d } from '../core/canvas2d';

/**
 * The one rounded, gradient-filled label plate every flat surface in this project builds off
 * of — extracted out of `PaletteUI.ts`, where it was born (plan/tray-categories.md, "What the
 * items look like" — the owner's own complaint: "floating rectangular text boxes side by side
 * ... doesn't look too good"), so a *second* consumer that only wants the plate — not the tray's
 * own wheel/grid/dwell machinery — does not have to import a whole `PaletteUI` module to get it.
 *
 * `Carousel.ts` is why this module exists as its own file rather than staying a `PaletteUI`
 * export: `PaletteUI` is about to *depend on* `Carousel` (its own category wheel becomes one),
 * so `Carousel` cannot import a plate-builder that still lives inside `PaletteUI.ts` without a
 * circular import. `DebugPanel.ts` and `ModeRingRenderer`'s own carousel both import this module
 * directly now too, for the identical reason — one rounded-plate construction, never a second
 * hand-rolled copy of the -Z/`DOUBLESIDE` gotchas CLAUDE.md's own labelling notes warn are easy
 * to get wrong twice.
 *
 * `buildMultiLinePlate`/`repaintMultiLinePlate` (plan/in-room-signage.md, the admin error log)
 * are the second shape this file builds: the same rounded chrome, mesh, texture and material
 * construction as `buildLabelledPlate`, but painting several left-aligned rows instead of one
 * centred label — `ErrorLogPanel.ts`'s own running list needs more than one line and
 * `buildLabelledPlate`'s signature has no way to say that. `buildPlateMesh` below is the
 * factored-out piece the two share; splitting it out here, rather than writing a second
 * near-identical `CreatePlane`/`DynamicTexture`/`StandardMaterial` construction inside
 * `ErrorLogPanel.ts`, is what keeps the -Z/`DOUBLESIDE` chrome in exactly one place.
 * `buildLabelledPlate`'s own body is unchanged in behaviour by this split — same texture-size
 * formula, same draw calls, same output for every existing caller.
 */

/** `Mesh.DOUBLESIDE`, inlined so this module needs no value import of Mesh. */
const DOUBLE_SIDED = 2;

/** Rounded-plate chrome (plan/tray-categories.md, "What the items look like"). Radius is a
 *  quarter of the plate's own height, in texture pixels, and never half — "a pill is the bubbly
 *  register design-conventions.md rejects... tight radii read as drafted; heavy radii read as a
 *  different product." */
const PLATE_CORNER_RADIUS_FRACTION = 0.25;

/**
 * The construction every plate this file builds shares: a double-sided plane with `backUVs`
 * flipped (CLAUDE.md, "A Babylon plane's front face is -Z" and "`DOUBLESIDE` plus disabled
 * `backFaceCulling`..."), a `DynamicTexture` sized off the plate's own aspect ratio, and an
 * unlit `StandardMaterial` reading that texture for both diffuse and emissive. `paint` is the
 * only thing that varies between `buildLabelledPlate`'s single centred label and
 * `buildMultiLinePlate`'s stacked rows — everything else, including `backFaceCulling` staying
 * at the material default (`true`), is identical for both, which is the whole point of
 * factoring this out rather than writing a second copy.
 */
function buildPlateMesh(
  scene: Scene,
  name: string,
  width: number,
  height: number,
  paint: (context: CanvasRenderingContext2D, textureWidth: number, textureHeight: number) => void,
): Mesh {
  const mesh = CreatePlane(
    name,
    {
      width,
      height,
      sideOrientation: DOUBLE_SIDED,
      frontUVs: new Vector4(0, 0, 1, 1),
      backUVs: new Vector4(1, 0, 0, 1),
    },
    scene,
  );

  const textureWidth = 280;
  const textureHeight = Math.round((height / width) * textureWidth);
  const texture = new DynamicTexture(name, { width: textureWidth, height: textureHeight }, scene, true);
  texture.hasAlpha = true;

  const context = as2d(texture.getContext());
  paint(context, textureWidth, textureHeight);
  texture.update();

  const material = new StandardMaterial(name, scene);
  material.diffuseTexture = texture;
  material.useAlphaFromDiffuseTexture = true;
  material.emissiveTexture = texture;
  material.disableLighting = true;
  material.specularColor = Color3.Black();
  mesh.material = material;

  return mesh;
}

/**
 * A labelled card, rounded rather than a hard-edged block. Painted as a rounded-rect path into
 * the DynamicTexture, with a shallow vertical gradient instead of a flat fill and a hairline
 * stroke just inside the rounded edge, leaving the corners themselves transparent — `hasAlpha`
 * on the texture and `useAlphaFromDiffuseTexture` on the material carry that transparency
 * through, the identical pairing `LabelRenderer.ts` already uses for its own translucent
 * backdrop. Still one plane, still one mesh, still double-sided with the back's `u` flipped, so
 * it reads forwards whichever face is seen.
 */
export function buildLabelledPlate(
  scene: Scene,
  name: string,
  label: string,
  background: string,
  ink: string,
  width: number,
  height: number,
): Mesh {
  return buildPlateMesh(scene, name, width, height, (context, textureWidth, textureHeight) =>
    paintRoundedPlate(context, textureWidth, textureHeight, background, ink, label),
  );
}

/**
 * The same rounded chrome as `buildLabelledPlate`, stacking `lines` top-to-bottom instead of
 * centring one label — `ErrorLogPanel.ts`'s own running list of recent errors/notices (newest
 * first) is the one caller. Each row is left-aligned and truncated with an ellipsis rather than
 * wrapped: a wall panel meant to be glanced at gains nothing from wrapping a long error message
 * into several lines of small text, and wrapping would also make the fixed `MAX_LINES` row
 * budget `ErrorLogPanel.ts` relies on unpredictable.
 */
export function buildMultiLinePlate(
  scene: Scene,
  name: string,
  lines: readonly string[],
  background: string,
  ink: string,
  width: number,
  height: number,
): Mesh {
  return buildPlateMesh(scene, name, width, height, (context, textureWidth, textureHeight) =>
    paintMultiLinePlate(context, textureWidth, textureHeight, background, ink, lines),
  );
}

/**
 * Repaints an existing `buildMultiLinePlate` mesh's texture in place, for content that changes
 * more often than a rebuild is worth — `ErrorLogPanel.record()` calls this on every new
 * message, rather than disposing and rebuilding the mesh the way `DebugPanel.ts`'s own
 * `updateGlowButtonLabel`/`updateGridToggleButtonLabel` do for their much rarer relabels.
 * `DynamicTexture.getSize()` is read back rather than assumed, so this always repaints at the
 * exact pixel dimensions the mesh was actually built with. A no-op if `mesh` was not built by
 * `buildMultiLinePlate` (or `buildPlateMesh` generally) — defensive, not a path any real caller
 * in this project reaches.
 */
export function repaintMultiLinePlate(
  mesh: Mesh,
  lines: readonly string[],
  background: string,
  ink: string,
): void {
  const material = mesh.material;

  if (!(material instanceof StandardMaterial)) {
    return;
  }

  const texture = material.diffuseTexture;

  if (!(texture instanceof DynamicTexture)) {
    return;
  }

  const { width: textureWidth, height: textureHeight } = texture.getSize();
  const context = as2d(texture.getContext());
  paintMultiLinePlate(context, textureWidth, textureHeight, background, ink, lines);
  texture.update();
}

/** Disposes a plate mesh's own texture and material along with the mesh itself — the
 *  `Node.dispose()` default only tears down the mesh (CLAUDE.md, "That same cascade does not
 *  free materials"), so every caller that builds a plate through `buildLabelledPlate` disposes
 *  it through this instead of a bare `mesh.dispose()`. */
export function disposePlate(mesh: Mesh): void {
  const material = mesh.material;

  if (material instanceof StandardMaterial) {
    material.diffuseTexture?.dispose();
  }

  material?.dispose();
  mesh.dispose();
}

function lighten(color: Color3, amount: number): Color3 {
  return new Color3(Math.min(1, color.r + amount), Math.min(1, color.g + amount), Math.min(1, color.b + amount));
}

function darken(color: Color3, amount: number): Color3 {
  return new Color3(Math.max(0, color.r - amount), Math.max(0, color.g - amount), Math.max(0, color.b - amount));
}

/** Traces a rounded-rectangle path via `arcTo`, not the newer `CanvasRenderingContext2D.
 *  roundRect` — this has to run in a headless canvas mock in tests as well as a real browser, and
 *  `arcTo` is the older, more universally-implemented primitive. */
function tracePlatePath(context: CanvasRenderingContext2D, width: number, height: number, radius: number): void {
  context.beginPath();
  context.moveTo(radius, 0);
  context.lineTo(width - radius, 0);
  context.arcTo(width, 0, width, radius, radius);
  context.lineTo(width, height - radius);
  context.arcTo(width, height, width - radius, height, radius);
  context.lineTo(radius, height);
  context.arcTo(0, height, 0, height - radius, radius);
  context.lineTo(0, radius);
  context.arcTo(0, 0, radius, 0, radius);
  context.closePath();
}

/**
 * The rounded, gradient-filled, hairline-stroked background every plate in this file shares —
 * plan/tray-categories.md, "What the items look like." The corners themselves are left
 * untouched by `clearRect`, i.e. transparent, which is what `hasAlpha`/
 * `useAlphaFromDiffuseTexture` on the caller's texture and material carry through to a
 * genuinely rounded mesh edge rather than a rounded rectangle painted onto a still-square,
 * opaque plate. Factored out of `paintRoundedPlate` so `paintMultiLinePlate` gets the identical
 * chrome without a second copy of the gradient/stroke construction.
 */
function paintPlateChrome(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  background: string,
): void {
  context.clearRect(0, 0, width, height);

  const radius = Math.min(height * PLATE_CORNER_RADIUS_FRACTION, width / 2, height / 2);
  tracePlatePath(context, width, height, radius);

  const base = Color3.FromHexString(background);

  // A shallow vertical gradient instead of a flat fillStyle, so the plate reads as a lit
  // surface rather than a flat block.
  const gradient = context.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, lighten(base, 0.08).toHexString());
  gradient.addColorStop(1, darken(base, 0.05).toHexString());
  context.fillStyle = gradient;
  context.fill();

  // A hairline stroke just inside the rounded edge, a shade off the fill, so the plate has an
  // edge rather than dissolving into the room.
  context.strokeStyle = darken(base, 0.22).toHexString();
  context.lineWidth = Math.max(1, height * 0.02);
  context.stroke();
}

/** `paintPlateChrome` plus one centred label — `buildLabelledPlate`'s own paint function,
 *  unchanged in output from before `paintPlateChrome` was split out (same draw calls, same
 *  order). */
function paintRoundedPlate(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  background: string,
  ink: string,
  label: string,
): void {
  paintPlateChrome(context, width, height, background);

  if (label.length > 0) {
    context.fillStyle = ink;
    context.font = `bold ${Math.round(height * 0.44)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(label, width / 2, height / 2 + 1);
  }
}

/** `paintPlateChrome` plus `lines.length` left-aligned rows, evenly spaced top to bottom —
 *  `buildMultiLinePlate`'s own paint function. A monospace font, unlike the single-label
 *  plate's proportional one: these rows are short diagnostic strings read one after another,
 *  and a fixed character width makes `truncateToWidth`'s own binary trim predictable rather
 *  than depending on which characters happen to be in a given error message. */
function paintMultiLinePlate(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  background: string,
  ink: string,
  lines: readonly string[],
): void {
  paintPlateChrome(context, width, height, background);

  if (lines.length === 0) {
    return;
  }

  const rowHeight = height / lines.length;
  const fontPx = Math.max(8, Math.round(rowHeight * 0.62));
  const paddingPx = Math.max(2, Math.round(rowHeight * 0.18));

  context.fillStyle = ink;
  context.font = `${fontPx}px "Consolas", "Courier New", monospace`;
  context.textAlign = 'left';
  context.textBaseline = 'middle';

  lines.forEach((line, index) => {
    const y = rowHeight * index + rowHeight / 2;
    context.fillText(truncateToWidth(context, line, width - paddingPx * 2), paddingPx, y);
  });
}

/** Trims `text` to fit `maxWidth`, appending an ellipsis, rather than letting a long line spill
 *  past the plate's own rounded edge (`buildPlateMesh`'s texture has no clipping of its own —
 *  a canvas draws past its nominal bounds without complaint). A linear trim from the end, one
 *  character at a time: these strings are short diagnostic messages, not user-facing prose
 *  worth a binary search over. */
function truncateToWidth(context: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0 || context.measureText(text).width <= maxWidth) {
    return text;
  }

  const ellipsis = '…';
  let end = text.length;

  while (end > 0 && context.measureText(text.slice(0, end) + ellipsis).width > maxWidth) {
    end -= 1;
  }

  return text.slice(0, end) + ellipsis;
}
